
import { Component, Mixins, Ref } from 'vue-property-decorator';
import CompanyPage from '@hokify/shared-components/lib/components/CompanyPage.vue';
import Lightbox from '@hokify/shared-components/lib/components/Lightbox.vue';
import type { IAPICompanyMedia, IAPICompanySnippet } from '@hokify/common';
import type { IMediaContent } from '@hokify/shared-components/lib/types/lightBox';
import JoblistMixin from '~/mixins/Joblist';
import { convertNumber, joinDifferentSeparators, joinFields, joinFilters } from '~/helpers/utility';
import { getCountryByCode } from '~/helpers/countryLookup';
import { getLocationText } from '@hokify/shared-components/lib/helpers/company-getter';
import { shortenString } from '@hokify/shared-components/lib/helpers/stringhelper';

type MapState = 'image' | 'map';

function hasOpenLightbox(obj: any): obj is { openLightbox(obj): void } {
	return typeof obj.openLightbox === 'function';
}

@Component({
	name: 'CompanyJobsPage',
	layout: 'plain-user',
	scrollToTop: true,
	components: {
		CompanyPage,
		Lightbox,
		CallToAction: () => import('@hokify/shared-components/lib/components/CallToAction.vue')
	}
})
export default class CompanyJobsPage extends Mixins(JoblistMixin) {
	@Ref('lightbox') readonly lightbox!: typeof Lightbox;

	mapState: MapState = 'image';

	isCompanyPage = true;

	companyGone = false;

	lightboxIsOpen = false;

	validMediaType = ['youtube', 'link', 'image'];

	companyLocationSuggestionsWrapper(term: string): Promise<string[]> {
		return this.companyLocationSuggestions({
			term,
			companyId: this.company._id
		});
	}

	openMedia(obj: IMediaContent) {
		if (this.validMediaType.includes(obj.type)) {
			if (hasOpenLightbox(this.lightbox)) {
				this.lightbox?.openLightbox(obj);
			}
		} else {
			this.$sentry.captureMessage(`company media: type ${obj.type} is not supported!`);
		}
	}

	rowItemClicked(): void {
		this.$trackWebsiteEvent('click_job', {});
		this.jobItemClicked(this.apiData?.lpId);
	}

	handleCompanyJobSearch(): void {
		// only initiate jobsearch if user makes new search query
		if (
			this.currentJobField !== this.initialJobField ||
			this.currentLocationName !== this.initialLocationName
		) {
			this.loading = true;
			this.$router.push({
				path: `/c/${this.company.alias}/search`,
				query: {
					branch: this.currentJobField,
					city: this.currentLocationName,
					radius: this.currentRadius
				}
			});
			this.$nextTick(() => {
				this.$scrollTo('#jobs', 300, {
					offset: this.$isMobile.any ? -20 : -85
				});
			});
		}
	}

	jobSearchViaQuery(): void {
		this.$router.push({
			path: '/jobs/search',
			query: {
				branch: this.initialJobField,
				city: this.initialLocationName
			}
		});
	}

	handleJobalarmClick() {
		this.observer?.disconnect();
		this.activateJobalarm('company-cta');
	}

	get media(): IAPICompanyMedia[] | undefined {
		return this.company?.about?.media;
	}

	get titleBefore(): string {
		let first = true;
		let titleBefore = '';
		if (this.apiData) {
			this.apiData.selected.filters.forEach(filter => {
				if (!filter.name.startsWith('company-')) {
					if (first && filter.titlePosition !== 'after') {
						titleBefore += convertNumber(filter.formatted);
						first = false;
					} else if (filter.titlePosition !== 'after') {
						titleBefore += `, ${convertNumber(filter.formatted)}`;
					}
				}
			});
		}
		return titleBefore;
	}

	get jobCount(): number {
		return this.apiData?.relationCount || 0;
	}

	// remove company logos for joblist on company page
	get leanJobs() {
		const leanJobs = [...this.relations];

		leanJobs.forEach(j => {
			if (j.type === 'job') {
				delete j.obj?.company?.logos;
			}
		});
		return leanJobs;
	}

	resetFilters(): void {
		this.currentSelectedFilters = this.currentSelectedFilters.filter(
			f => f.filter === `company-${this.company.alias}`
		);
		this.handleJobSearch();
	}

	fieldClicked(value) {
		this.currentJobField = value;
		this.handleCompanyJobSearch();
	}

	get selectedFiltersBesidesCompany(): boolean {
		return (
			this.currentSelectedFilters.filter(f => f.filter !== `company-${this.company.alias}`).length >
			0
		);
	}

	get filteredSimilarCompanies(): IAPICompanySnippet[] {
		return this.apiData?.similarCompanies?.filter(c => c.alias && c.name);
	}

	get additionalData() {
		return {
			jobCount: this.jobCount,
			useBodyContainer: true
		};
	}

	get jobCountTitle(): string {
		let jobCountTitle = 'Wir haben aktuell ';

		if (this.apiData?.relationCount === 0 || this.error) {
			jobCountTitle += `keine ${this.initialJobField} Jobs`;
		} else if (this.apiData?.relationCount === 1) {
			jobCountTitle += `einen ${this.initialJobField} Job`;
		} else {
			jobCountTitle += `${this.apiData?.relationCount} ${this.initialJobField} Jobs`;
		}
		jobCountTitle += this.initialLocationName ? ` in ${this.initialLocationName}` : '';
		jobCountTitle += this.jobCount ? ':' : '';
		return jobCountTitle;
	}

	get pageTitle(): string {
		let pageTitle = '';
		if (this.companyGone) {
			pageTitle = 'Firmenseite existiert nicht';
		} else if (this.apiData) {
			pageTitle = this.company.name;
			const { selected, maxWage, minWage } = this.apiData;
			if (selected?.branch?.formatted) {
				pageTitle += ` ${selected.branch.formatted}`;
			}

			if (selected?.filters?.length > 0) {
				const filterText = `${joinFilters(
					selected.filters
						.filter(f => !f.name.startsWith('company-'))
						.map(f => f.formatted.replace('in der Branche ', '')),
					', ',
					'und',
					selected.filters.length
				)}`.trim();
				if (filterText?.length > 0) {
					pageTitle += ` ${filterText}`;
				}
			}

			pageTitle += ' Jobs';

			if (selected?.city?.formatted) {
				pageTitle += ` in ${selected.city.formatted}`;
			}

			if (minWage || maxWage) {
				pageTitle += ' - Stellenangebote';
				if (minWage && minWage !== maxWage) {
					pageTitle += ` von ${minWage}`;
				}

				if (maxWage) {
					pageTitle += ` bis ${maxWage}`;
				}
				pageTitle += ' EUR';
			}

			const postFix = getCountryByCode(this.$store.state.topLevelDomain);
			if (postFix && !pageTitle.includes(postFix)) {
				pageTitle += ` ${postFix}`;
			}
		}

		return pageTitle.trim();
	}

	get pageDescription(): string {
		let pageDescription = '';
		if (this.apiData) {
			const { allPossibleFields, relationCount, selected, allTypes } = this.apiData;
			if (allPossibleFields) {
				pageDescription = `Jobs ${joinFields(this.apiData.allPossibleFields, ', ', 'und', 7)}.`;
			}

			if (relationCount > 0) {
				const beautifyCnt = Math.floor(relationCount / 10) * 10;
				if (beautifyCnt > 0) {
					pageDescription += ` ${beautifyCnt}+`;
				} else {
					pageDescription += ` ${relationCount}`;
				}
			}

			if (selected?.branch?.formatted) {
				pageDescription += ` ${selected.branch.formatted}`;
			}

			pageDescription += relationCount === 1 ? ' Job' : ' Jobs';

			if (selected?.city?.formatted) {
				pageDescription += ` in ${selected.city.formatted}`;
			}

			if (allTypes?.length > 0) {
				pageDescription += ` ${joinDifferentSeparators(allTypes, ', ', 'und')}`;
			}

			if (selected?.filters?.length > 0) {
				const filterText = `${joinFilters(
					selected.filters
						.filter(f => !f.name.startsWith('company-'))
						.map(f => f.formatted.replace('in der Branche ', '')),
					', ',
					'und',
					selected.filters.length
				)}`.trim();
				if (filterText?.length > 0) {
					pageDescription += ` ${filterText}`;
				}
			}

			if (this.company?.name) {
				pageDescription += ` bei ${this.company.name}`;
			}

			const postFix = getCountryByCode(this.$store.state.topLevelDomain);
			if (postFix && !pageDescription.includes(postFix)) {
				pageDescription += ` ${postFix}`;
			}

			pageDescription += '.';

			const additionalText = [
				' In 30 Sekunden bewerben, auch mobil.',
				' Hokify ist deine mobile Job-Plattform.'
			];

			if (pageDescription.length + additionalText[0].length <= 260) {
				pageDescription += additionalText[0];
			}

			if (pageDescription.length + additionalText[1].length <= 260) {
				pageDescription += additionalText[1];
			}
		}

		return pageDescription.trim();
	}

	get socialsTitle(): string {
		return this.apiData ? shortenString(this.company?.name || '', 60) : '';
	}

	get socialsDescription(): string {
		return this.apiData && this.company
			? shortenString(
					`Spannendes Unternehmen: ${this.company?.name} ${
						getLocationText(this.company) ? ` · ${getLocationText(this.company)}` : ''
					} · Jetzt bewerben!`,
					155
				)
			: '';
	}

	get socialsImage(): string {
		return this.apiData ? this.company?.coverImage?.medium || '' : '';
	}
}
